import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'antd';

import { useIntl } from 'common/config/i18n';
import {
  MaskActions,
  MasksConfigSelectors,
  MaskSelectors,
  MasksSelectors,
  TranslationLanguagesSelectors,
} from 'common/config/store';
import FormMask, { FormValues } from 'components/templates/form-mask';

interface Props {
  maskId: number;
  isOpen: boolean;
  onClose: () => void;
  isStorybook?: boolean;
}

const shouldDeleteFile = (
  fileName: string | null | undefined,
  currentFile: File | undefined,
  savedFileName: string | undefined
): boolean => {
  return !!fileName && !currentFile && !savedFileName;
};

const EditMaskModal: React.FC<Props> = ({ maskId, isOpen, onClose, isStorybook = false }) => {
  const dispatch = useDispatch();
  const { t } = useIntl();

  const childRef = useRef<HTMLFormElement>(null);

  const currentTemplate = useSelector(MasksSelectors.TemplateByMaskIdSelector(maskId));
  const masksConfig = useSelector(MasksConfigSelectors.masksConfigSelector);
  const maskStatus = useSelector(MaskSelectors.statusSelector);
  const translationLanguages = useSelector(TranslationLanguagesSelectors.translationLanguagesSelector);

  const onSubmitHandler = (values: FormValues) => {
    if (!isStorybook) {
      dispatch(
        MaskActions.updateMask({
          id: maskId,
          mailConfiguration: {
            defaultObject: values.mailDefaultObject,
            defaultHeadband: values.defaultHeadband,
            enabledByDefault: !!values.mailEnabledByDefault,
            file: values.mailFile,
            deleteFile: shouldDeleteFile(
              currentTemplate?.mailConfiguration?.fileName,
              values.mailFile,
              values.mailFileNameSaved
            ),
            deleteDefaultObject: values.mailDefaultObject?.trim() === '',
            // Add mailConfig translation specific params
            translations: translationLanguages
              .map((lang) => {
                const mailConfigurationTranslationFileName = currentTemplate?.mailConfiguration?.translations?.find(
                  (translation) => translation.languageCode === lang.code
                )?.fileName;
                return {
                  languageCode: lang.code,
                  file: values[`${lang.code}-mailFile`],
                  deleteFile: shouldDeleteFile(
                    mailConfigurationTranslationFileName,
                    values[`${lang.code}-mailFile`],
                    values[`${lang.code}-mailFileNameSaved`]
                  ),
                  defaultObject: values[`${lang.code}-mailDefaultObject`],
                  deleteDefaultObject: values[`${lang.code}-mailDefaultObject`]?.trim() === '',
                };
              })
              .filter(
                (translation) =>
                  !!translation.file ||
                  !!translation.defaultObject ||
                  translation.defaultObject === '' ||
                  !!translation.deleteFile ||
                  !!translation.deleteDefaultObject
              ),
          },
          smsConfiguration: {
            enabledByDefault: values.smsEnabledByDefault,
            file: values.smsFile,
            deleteFile: shouldDeleteFile(
              currentTemplate?.smsConfiguration?.fileName,
              values.smsFile,
              values.smsFileNameSaved
            ),
          },
          whatsappConfiguration: {
            enabledByDefault: values.whatsAppEnabledByDefault,
            maskFile: values.whatsappMaskFile,
            deleteMaskFile: shouldDeleteFile(
              currentTemplate?.whatsappConfiguration?.maskFileName,
              values.whatsappMaskFile,
              values.whatsappMaskFileNameSaved
            ),
            refFile: values.whatsappRefFile,
            deleteRefFile: shouldDeleteFile(
              currentTemplate?.whatsappConfiguration?.refFileName,
              values.whatsappRefFile,
              values.whatsappRefFileNameSaved
            ),
          },
          flashEnabled: values.flashEnabledByDefault,
        })
      );
    }
  };

  const onCloseModal = () => {
    onClose();
    childRef?.current?.resetForm();
  };

  const title = t('MaskPage', 'MASK_UPDATE');

  return (
    <Modal title={title} open={isOpen} closable footer={null} onCancel={onCloseModal} width={800}>
      <FormMask
        ref={childRef}
        onSubmit={(...args) => {
          onSubmitHandler(...args);
          onClose();
        }}
        maskTemplate={currentTemplate}
        translationLanguages={translationLanguages}
        masksConfig={masksConfig}
        status={maskStatus}
      />
    </Modal>
  );
};

export default EditMaskModal;
